/**
 * Catalogo de eventos da plataforma.
 *
 * Os plugins comunicam entre si por eventos, nunca por importacao direta
 * (ADR-0002). Um plugin de saude emite `health.medication.taken`; o plugin de
 * automacao ouve e decide avisar a familia. Nenhum dos dois conhece o outro.
 *
 * Convencao de nomes: `<origem>.<entidade>.<fato-no-passado>`.
 */

export const CoreEvents = {
  /** O aplicativo terminou o arranque e todos os plugins estao registrados. */
  READY: 'core.app.ready',
  /** Mudou o membro ativo ou a familia ativa. */
  SESSION_CHANGED: 'core.session.changed',
  /** Perdeu-se ou recuperou-se a ligacao. payload: { online: boolean } */
  CONNECTIVITY_CHANGED: 'core.connectivity.changed',
  /** Um registro foi escrito localmente. payload: RecordChangedPayload */
  RECORD_CHANGED: 'core.record.changed',
  /** O motor de sync mudou de fase. payload: SyncStatus */
  SYNC_STATUS_CHANGED: 'core.sync.status',
  /** Chegou um conflito que precisa de decisao humana. payload: ConflictReport */
  SYNC_CONFLICT: 'core.sync.conflict',
  /** A IA propos uma acao. payload: AiAction */
  AI_ACTION_PROPOSED: 'core.ai.proposed',
  /** O usuário confirmou uma acao da IA. payload: AiAction */
  AI_ACTION_CONFIRMED: 'core.ai.confirmed',
  /** O usuário rejeitou uma acao da IA — sinal de aprendizagem. */
  AI_ACTION_REJECTED: 'core.ai.rejected',
  /** Erro nao tratado, capturado pela fronteira de erros. */
  ERROR: 'core.error',
  /** Uma definicao mudou. payload: { namespace, key, value } */
  SETTINGS_CHANGED: 'core.settings.changed',
  /** Mudou o idioma/moeda/fuso. payload: LocaleConfig */
  LOCALE_CHANGED: 'core.locale.changed',
  /** Um plugin nao arrancou. payload: { pluginId, problems } */
  PLUGIN_FAILED: 'core.plugin.failed',
  /** Todos os plugins terminaram o registro. payload: { loaded, failed, skipped } */
  PLUGINS_STARTED: 'core.plugins.started',
} as const;

export interface RecordChangedPayload {
  entity: string;
  recordId: string;
  operation: 'create' | 'update' | 'delete' | 'restore';
  /**
   * Origem da escrita — permite ignorar ecos do proprio sync.
   * Alinhado com `AuditActor`: uma escrita do sistema tem de poder dizer-se
   * `'system'` em vez de se disfarcar de automacao.
   */
  source: 'user' | 'ai' | 'automation' | 'sync' | 'system';
  record: unknown;
}

/** Eventos que os plugins de negocio emitem. Aqui para haver um lugar unico. */
export const PluginEvents = {
  // Familia (Milestone 2)
  FAMILY_MEMBER_JOINED: 'family.member.joined',
  FAMILY_MEMBER_LEFT: 'family.member.left',
  FAMILY_ROLE_CHANGED: 'family.member.role_changed',

  // Financas (Milestone 4)
  FINANCE_TRANSACTION_ADDED: 'finance.transaction.added',
  FINANCE_BUDGET_EXCEEDED: 'finance.budget.exceeded',
  FINANCE_SUSPICIOUS_CHARGE: 'finance.charge.suspicious',
  FINANCE_RECEIPT_IMPORTED: 'finance.receipt.imported',

  // Saude (Milestone 5)
  HEALTH_MEDICATION_TAKEN: 'health.medication.taken',
  HEALTH_MEDICATION_OVERDUE: 'health.medication.overdue',
  HEALTH_MEDICATION_SKIPPED: 'health.medication.skipped',
  HEALTH_READING_RECORDED: 'health.reading.recorded',
  HEALTH_READING_OUT_OF_RANGE: 'health.reading.out_of_range',
  HEALTH_APPOINTMENT_SOON: 'health.appointment.soon',

  // Inventario e compras (Milestones 7)
  INVENTORY_LOW_STOCK: 'inventory.item.low_stock',
  SHOPPING_ITEM_ADDED: 'shopping.item.added',

  // Documentos (Milestone 6)
  DOCUMENT_SCANNED: 'documents.document.scanned',
  DOCUMENT_EXPIRING: 'documents.document.expiring',

  // Viagem (Milestone 8)
  TRAVEL_TRIP_CREATED: 'travel.trip.created',

  // Email (Milestone 9)
  EMAIL_SCAM_DETECTED: 'email.message.scam_detected',
  EMAIL_BILL_DETECTED: 'email.message.bill_detected',
} as const;

export type CoreEventName = (typeof CoreEvents)[keyof typeof CoreEvents];
export type PluginEventName = (typeof PluginEvents)[keyof typeof PluginEvents];
export type EventName = CoreEventName | PluginEventName | (string & {});
